import { useEffect, useMemo, useState } from 'react';
import {
  fetchFactionAssets,
  fetchFactionProfile,
  fetchLoadouts,
  rosterOf,
  ROSTER_ROLES,
  ROSTER_STATIC_KINDS,
  searchVehicles,
  setFactionRoster,
  type FactionProfileView,
  type FactionRoster as Roster,
  type LoadoutView,
  type VehicleHit,
} from '../api/catalog';
import { ApiError } from '../api/http';
import { Link } from 'react-router-dom';
import { ca } from '../i18n/ca';
import { ModOriginChip } from './ModOrigin';
import { modOriginLabel } from './modOriginLabel';
import { useConfirm } from './useConfirm';

/**
 * A hand-made enemy faction's roster (docs/design/HANDMADE_ENEMY_FACTIONS.md):
 * the men its groups are made of, each a class of any faction with a role, a
 * loadout of the catalogue and a weight; the ground vehicles it rides, of any
 * faction; and its statics by the kind the runtime asks for. The platform
 * checks every class against the server's catalogue when it is saved, and says
 * why when it refuses.
 */
export function FactionRosterEditor({
  profile,
  modSetHash,
  onSaved,
  onClose,
}: {
  profile: FactionProfileView;
  modSetHash: string;
  onSaved: (view: FactionProfileView) => void;
  onClose: () => void;
}) {
  const r = ca.factionRoster;
  const [roster, setRoster] = useState<Roster | null>(null);
  const [loadouts, setLoadouts] = useState<LoadoutView[]>([]);
  const [men, setMen] = useState<VehicleHit[]>([]);
  const [vehicles, setVehicles] = useState<VehicleHit[]>([]);
  const [statics, setStatics] = useState<VehicleHit[]>([]);
  const [manQuery, setManQuery] = useState('');
  const [vehicleQuery, setVehicleQuery] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, dialog] = useConfirm();

  useEffect(() => {
    let live = true;
    void fetchFactionProfile(profile.id)
      .then((d) => live && setRoster(rosterOf(d.profileJson)))
      .catch(() => live && setRoster({ men: [], vehicles: [], statics: [] }));
    // Every loadout, whatever mod set it was saved under: a roster names its kit by id.
    void fetchLoadouts()
      .then((l) => live && setLoadouts(l))
      .catch(() => undefined);
    void searchVehicles(modSetHash, { kind: 'static', limit: 200 })
      .then((p) => live && setStatics(p.vehicles))
      .catch(() => undefined);
    // The base faction's own men first; any other faction's by name below.
    void fetchFactionAssets(modSetHash, profile.factionClass)
      .then(
        (a) =>
          live &&
          setMen((m) =>
            m.length > 0
              ? m
              : a.units.map((u) => ({
                  className: u.className,
                  displayName: u.displayName,
                  kind: 'man',
                  transportSoldier: 0,
                  factionClass: profile.factionClass,
                  origin: u.origin,
                })),
          ),
      )
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [profile.id, profile.factionClass, modSetHash]);

  useEffect(() => {
    if (manQuery.trim().length < 3) return;
    const t = setTimeout(() => {
      void searchVehicles(modSetHash, { kind: 'man', q: manQuery.trim(), limit: 80 })
        .then((p) => setMen(p.vehicles))
        .catch(() => undefined);
    }, 250);
    return () => clearTimeout(t);
  }, [manQuery, modSetHash]);

  useEffect(() => {
    const t = setTimeout(() => {
      void searchVehicles(modSetHash, { q: vehicleQuery.trim() || null, limit: 120 })
        .then((p) =>
          setVehicles(p.vehicles.filter((v) => ['car', 'apc', 'tank', 'land'].includes(v.kind))),
        )
        .catch(() => undefined);
    }, 250);
    return () => clearTimeout(t);
  }, [vehicleQuery, modSetHash]);

  const loadoutName = useMemo(
    () => new Map(loadouts.map((l) => [String(l.id), l.name])),
    [loadouts],
  );

  if (!roster) return <p className="muted small">…</p>;

  const set = (next: Partial<Roster>) => setRoster({ ...roster, ...next });
  const setMan = (i: number, change: Partial<Roster['men'][number]>) =>
    set({ men: roster.men.map((m, j) => (j === i ? { ...m, ...change } : m)) });
  const noLeader = roster.men.length > 0 && !roster.men.some((m) => m.role === 'leader');

  const save = async (next: Roster | null) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await setFactionRoster(profile.id, next, modSetHash);
      setRoster(rosterOf(saved.profileJson));
      setMessage(next ? r.saved : r.cleared);
      onSaved(saved);
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.code) : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card form" role="group" aria-label={r.title(profile.name)}>
      {dialog}
      <div className="row between">
        <h3>{r.title(profile.name)}</h3>
        <button type="button" onClick={onClose}>
          {r.close}
        </button>
      </div>
      <p className="muted small">{r.intro}</p>

      <h4>{r.menTitle}</h4>
      <label>
        {r.search}
        <input
          name="roster-man-search"
          value={manQuery}
          placeholder="Tura, rhsgref, CUP…"
          onChange={(e) => setManQuery(e.target.value)}
        />
      </label>
      <datalist id={`roster-men-${profile.id}`}>
        {men.map((m) => (
          <option key={m.className} value={m.className}>
            {m.displayName}
            {m.factionName ? ` — ${m.factionName}` : ''}
            {m.origin ? ` · ${modOriginLabel(m.origin)}` : ''}
          </option>
        ))}
      </datalist>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{r.unitClass}</th>
              <th>{r.role}</th>
              <th>{r.label}</th>
              <th>{r.loadout}</th>
              <th>{r.weight}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {roster.men.map((m, i) => (
              <tr key={i}>
                <td>
                  <input
                    name={`roster-class-${i}`}
                    list={`roster-men-${profile.id}`}
                    value={m.unitClass}
                    aria-label={r.unitClass}
                    onChange={(e) => setMan(i, { unitClass: e.target.value.trim() })}
                  />{' '}
                  <ModOriginChip
                    origin={
                      men.find((x) => x.className.toLowerCase() === m.unitClass.toLowerCase())
                        ?.origin
                    }
                  />
                </td>
                <td>
                  <select
                    name={`roster-role-${i}`}
                    aria-label={r.role}
                    value={m.role}
                    onChange={(e) => setMan(i, { role: e.target.value })}
                  >
                    {ROSTER_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {r.roles[role] ?? role}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    name={`roster-label-${i}`}
                    aria-label={r.label}
                    maxLength={60}
                    value={m.label ?? ''}
                    onChange={(e) => setMan(i, { label: e.target.value || undefined })}
                  />
                </td>
                <td>
                  <select
                    name={`roster-loadout-${i}`}
                    aria-label={r.loadout}
                    value={m.loadoutId ?? ''}
                    onChange={(e) => setMan(i, { loadoutId: e.target.value || undefined })}
                  >
                    <option value="">{r.loadoutOwn}</option>
                    {m.loadoutId && !loadoutName.has(m.loadoutId) && (
                      <option value={m.loadoutId}>#{m.loadoutId}</option>
                    )}
                    {loadouts.map((l) => (
                      <option key={l.id} value={String(l.id)}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  {m.loadoutId && (
                    <Link
                      className="small"
                      to={`/catalog/loadouts/${m.loadoutId}?hash=${modSetHash}`}
                      aria-label={ca.loadoutEditor.editKitFor(
                        loadoutName.get(m.loadoutId) ?? `#${m.loadoutId}`,
                      )}
                    >
                      <i className="bi bi-pencil" aria-hidden="true" /> {ca.loadoutEditor.editKit}
                    </Link>
                  )}
                </td>
                <td>
                  <input
                    name={`roster-weight-${i}`}
                    aria-label={r.weight}
                    type="number"
                    min={1}
                    max={10}
                    value={m.weight}
                    onChange={(e) =>
                      setMan(i, { weight: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })
                    }
                  />
                </td>
                <td>
                  <button
                    type="button"
                    aria-label={r.remove}
                    onClick={() => set({ men: roster.men.filter((_, j) => j !== i) })}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="row">
        <button
          type="button"
          disabled={roster.men.length >= 32}
          onClick={() =>
            set({
              men: [
                ...roster.men,
                { unitClass: '', role: roster.men.length === 0 ? 'leader' : 'rifleman', weight: 1 },
              ],
            })
          }
        >
          <i className="bi bi-plus-lg" aria-hidden="true" /> {r.add}
        </button>
        {noLeader && <span className="chip warn">{r.noLeader}</span>}
      </div>

      <h4>{r.vehiclesTitle}</h4>
      <p className="muted small">{r.vehiclesHint}</p>
      <ul className="list">
        {roster.vehicles.map((v) => (
          <li key={v}>
            <code className="grow">{v}</code>
            <button
              type="button"
              aria-label={r.remove}
              onClick={() => set({ vehicles: roster.vehicles.filter((x) => x !== v) })}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      <label>
        {r.search}
        <input
          name="roster-vehicle-search"
          value={vehicleQuery}
          placeholder="uaz, offroad, pickup…"
          onChange={(e) => setVehicleQuery(e.target.value)}
        />
      </label>
      <ul className="list">
        {vehicles
          .filter((v) => !roster.vehicles.includes(v.className))
          .slice(0, 12)
          .map((v) => (
            <li key={v.className}>
              <span className="grow">
                {v.displayName} <code className="muted small">{v.className}</code>
                {v.factionName && <span className="muted small"> — {v.factionName}</span>}{' '}
                <ModOriginChip origin={v.origin} />
              </span>
              <button
                type="button"
                disabled={roster.vehicles.length >= 32}
                onClick={() => set({ vehicles: [...roster.vehicles, v.className] })}
              >
                {r.add}
              </button>
            </li>
          ))}
      </ul>

      <h4>{r.staticsTitle}</h4>
      <p className="muted small">{r.staticsHint}</p>
      <datalist id={`roster-statics-${profile.id}`}>
        {statics.map((s) => (
          <option key={s.className} value={s.className}>
            {s.displayName}
            {s.factionName ? ` — ${s.factionName}` : ''}
          </option>
        ))}
      </datalist>
      <ul className="list">
        {roster.statics.map((s, i) => (
          <li key={i}>
            <input
              name={`roster-static-${i}`}
              className="grow"
              list={`roster-statics-${profile.id}`}
              aria-label={r.className}
              value={s.className}
              onChange={(e) =>
                set({
                  statics: roster.statics.map((x, j) =>
                    j === i ? { ...x, className: e.target.value.trim() } : x,
                  ),
                })
              }
            />
            <select
              name={`roster-static-kind-${i}`}
              aria-label={r.kind}
              value={s.kind}
              onChange={(e) =>
                set({
                  statics: roster.statics.map((x, j) =>
                    j === i ? { ...x, kind: e.target.value } : x,
                  ),
                })
              }
            >
              {ROSTER_STATIC_KINDS.map((k) => (
                <option key={k} value={k}>
                  {r.kinds[k] ?? k}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-label={r.remove}
              onClick={() => set({ statics: roster.statics.filter((_, j) => j !== i) })}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        disabled={roster.statics.length >= 16}
        onClick={() => set({ statics: [...roster.statics, { className: '', kind: 'hmg' }] })}
      >
        <i className="bi bi-plus-lg" aria-hidden="true" /> {r.add}
      </button>

      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={busy || roster.men.length === 0 || noLeader}
          onClick={() =>
            void save({
              men: roster.men.filter((m) => m.unitClass !== ''),
              vehicles: roster.vehicles,
              statics: roster.statics.filter((s) => s.className !== ''),
            })
          }
        >
          {r.save}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void confirm({ title: r.clear, body: r.clearConfirm, confirmLabel: r.clear }).then(
              (yes) => {
                if (yes) void save(null);
              },
            )
          }
        >
          {r.clear}
        </button>
        {message && <span className="muted small">{message}</span>}
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
